<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import { Button } from '@/components/ui/button';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Input } from '@/components/ui/input';
import { Dialog, DialogTrigger, DialogContent, DialogClose, DialogHeader, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue';
import { useDeleteAccount } from '@/composables/api/useProfileApi';
import { useAuthStore } from '@/stores/authStore';
import { clearTokens } from '@/services/tokenStorage';
import { AxiosError } from 'axios';

interface DeleteAccountErrors {
  password?: string[];
  [key: string]: string[] | undefined;
}

const router = useRouter();
const authStore = useAuthStore();
const { mutate: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount();

const errors = ref<DeleteAccountErrors>({});
const passwordInput = ref('');

function resetForm() {
  passwordInput.value = '';
  errors.value = {};
}

function deleteUser() {
  deleteAccount(
    {
      password: passwordInput.value
    },
    {
      onSuccess: () => {
        resetForm();
        // The account is gone and its tokens are now dead. Clear local auth state BEFORE
        // navigating: otherwise `isAuthenticated` stays true, the /account route guard bounces
        // /login → /home (a protected route), and that runs loadUser() with the dead token. The
        // resulting 401 → failed refresh triggers a full-page reload (services/http.ts) that wipes
        // the success toast and is jarring UX. Clearing here keeps the user on /login cleanly.
        clearTokens();
        authStore.$reset();
        router.push({ name: 'login' });
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError && error.response?.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          errors.value = {};
        }
      }
    }
  );
}
</script>

<template>
  <section class="space-y-6">
    <header>
      <h2 class="text-lg font-medium">Delete Account</h2>

      <p class="mt-1 text-sm">
        Once your account is deleted, all of its resources and data will be permanently deleted.
        Before deleting your account, please download any data or information that you wish to
        retain.
      </p>
    </header>

    <Dialog>
      <DialogTrigger as-child>
        <Button variant="destructive" :disabled="isDeletingAccount">Delete Account</Button>
      </DialogTrigger>
      <DialogContent class="max-w-md">
        <form class="mt-6" @submit.prevent="deleteUser">
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete your account?</DialogTitle>
            <DialogDescription>
              Once your account is deleted, all of its resources and data will be permanently deleted.
              Please enter your password to confirm you would like to permanently delete your account.
            </DialogDescription>
          </DialogHeader>
          <Input id="password" type="password" :modelValue="passwordInput" class="mt-1 block w-full" required
            autocomplete="current-password" name="password" placeholder="Password" :disabled="isDeletingAccount"
            @update:modelValue="(value: string | number) => passwordInput = String(value)" />

          <InputError v-if="errors['password']" :message="errors['password'][0]" class="mt-2" />
          <DialogFooter class="mt-6 flex justify-end">
            <DialogClose as-child>
              <Button type="button" variant="secondary" :disabled="isDeletingAccount">Cancel</Button>
            </DialogClose>
            <Button type="submit" variant="destructive" :disabled="isDeletingAccount" :loading="isDeletingAccount">
              Delete Account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </section>
</template>
